import { useState } from 'react'
import { useMutation, useQueryClient, useQuery } from 'react-query'
import { CheckIcon, XMarkIcon, ArrowPathIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline'
import { ImageData, AnnotationStatus, SpaceType, ApproverStatus, IssueType } from '../types'
import { imageApi } from '../services/api'
import toast from 'react-hot-toast'
import ImageModal from './ImageModal'
import { IssueSelector } from './IssueSelector'

interface ImageGridProps {
  images: ImageData[]
  annotatorId: string
  onAnnotationUpdate: () => void
  currentSpace?: SpaceType
}

interface AnnotationFormData {
  status: AnnotationStatus
  notes?: string
  issues?: IssueType[]
}

const ImageGrid = ({ images, annotatorId, onAnnotationUpdate, currentSpace = 'annotator' }: ImageGridProps) => {
  
  const [editingIssues, setEditingIssues] = useState<Record<string, IssueType[]>>({})
  const [selectedImage, setSelectedImage] = useState<ImageData | null>(null)
  const queryClient = useQueryClient()

  // Fetch available issues
  const { data: availableIssuesData } = useQuery(
    'availableIssues',
    imageApi.getAvailableIssues,
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
    }
  )
  const availableIssues = availableIssuesData?.issues || []

  // Mutation for updating annotations
  const updateAnnotationMutation = useMutation(
    ({ imageId, data }: { imageId: string; data: AnnotationFormData }) =>
      imageApi.createAnnotation({
        image_id: imageId,
        status: data.status,
        notes: data.notes || undefined,
        issues: data.issues || undefined,
        annotator_id: annotatorId,
      }),
    {
      onSuccess: () => {
        onAnnotationUpdate()
        queryClient.invalidateQueries(['images'])
        queryClient.invalidateQueries(['annotation-stats'])
        setTimeout(() => {
          toast.success('Annotation saved successfully')
        }, 500)
      },
      onError: (error: any) => {
        console.error('Annotation update failed:', error)
        toast.error('Failed to save annotation')
      },
    }
  )

  // Mutation for updating approver status
  const updateApproverMutation = useMutation(
    ({ imageId, approverStatus }: { imageId: string; approverStatus: ApproverStatus }) =>
      imageApi.updateApproverStatus({
        image_id: imageId,
        approver_status: approverStatus,
        approver_id: annotatorId, // Using annotatorId as approver_id
      }),
    {
      onSuccess: () => {
        onAnnotationUpdate()
        queryClient.invalidateQueries(['images'])
        setTimeout(() => {
          toast.success('Approval status updated successfully')
        }, 500)
      },
      onError: (error: any) => {
        console.error('Approver update failed:', error)
        toast.error('Failed to update approval status')
      },
    }
  )

  const handleStatusChange = (imageId: string, status: AnnotationStatus) => {
    const image = images.find(img => img.image_id === imageId)
    const currentIssues = editingIssues[imageId] ?? image?.issues ?? []
    
    updateAnnotationMutation.mutate({
      imageId,
      data: { status, issues: currentIssues }
    })
  }

  const handleApproverStatusChange = (imageId: string, approverStatus: ApproverStatus) => {
    updateApproverMutation.mutate({
      imageId,
      approverStatus
    })
  }

  const handleIssuesChange = (imageId: string, issues: IssueType[]) => {
    setEditingIssues(prev => ({ ...prev, [imageId]: issues }))
    
    // Auto-save issues when they change
    const image = images.find(img => img.image_id === imageId)
    updateAnnotationMutation.mutate({
      imageId,
      data: { 
        status: image?.annotation_status || 'pending', 
        issues: issues 
      }
    })
  }

  

  const getStatusColor = (status: AnnotationStatus) => {
    switch (status) {
      case 'accepted':
        return 'text-green-600 bg-green-100 dark:bg-green-900 dark:text-green-300'
      case 'rejected':
        return 'text-red-600 bg-red-100 dark:bg-red-900 dark:text-red-300'
      case 'regenerate':
        return 'text-blue-600 bg-blue-100 dark:bg-blue-900 dark:text-blue-300'
      default:
        return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900 dark:text-yellow-300'
    }
  }

  // Function to display image ID - now shows product_id_color_id format
  const formatImageId = (imageId: string) => {
    // If it's already in the format product_id_color_id, return as is
    if (imageId.includes('_') && !imageId.includes('/')) {
      return imageId
    }
    // Otherwise, truncate to show only the part after the last slash (fallback for old format)
    const parts = imageId.split('/')
    return parts[parts.length - 1]
  }

  // Debug function to handle image clicks
  const handleImageClick = (image: ImageData) => {
    setSelectedImage(image)
  }

  // Debug function to handle modal close
  const handleModalClose = () => {
    setSelectedImage(null)
  }

  const ImageCell = ({ src, alt, image }: { src: string; alt: string; image: ImageData }) => (
    <div 
      className="image-container aspect-square relative group cursor-pointer"
      onClick={() => handleImageClick(image)}
    >
      <img
        src={src}
        alt={alt}
        className="w-full h-full object-cover hover:opacity-90 transition-opacity pointer-events-none"
        onError={(e) => {
          const img = e.target as HTMLImageElement
          img.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2Y3ZjdmNyIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBkb21pbmFudC1iYXNlbGluZT0iY2VudHJhbCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZmlsbD0iIzk5OTk5OSI+SW1hZ2UgTm90IEZvdW5kPC90ZXh0Pjwvc3ZnPg=='
        }}
      />
      
      <div className="image-overlay group-hover:bg-opacity-20 pointer-events-none">
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="bg-black bg-opacity-75 text-white px-3 py-1 rounded-lg text-sm flex items-center">
            <MagnifyingGlassIcon className="w-4 h-4 mr-1" />
            Click to enlarge
          </div>
        </div>
      </div>
    </div>
  )

  return (
    <>
      <div className="overflow-x-auto">
        <table className="min-w-[900px] xl:min-w-[1900px] w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              {/* Image ID - Hidden on tablets and mobile */}
              <th className="hidden xl:table-cell px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-36">
                Image ID
              </th>
              <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-36 xl:w-72">
                Base Image 1
              </th>
              <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-36 xl:w-72">
                Base Image 2
              </th>
              <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-36 xl:w-72">
                Result Image
              </th>
              <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-28 xl:w-52">
                Status
              </th>
              <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-32 xl:w-80">
                Issues
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
            {images.map((image) => {
              const currentIssues = editingIssues[image.image_id] ?? image.issues ?? []
              const formattedId = formatImageId(image.image_id)
              
              return (
                <tr key={image.image_id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                  {/* Image ID - Hidden on tablets and mobile */}
                  <td className="hidden xl:table-cell px-3 py-4 text-sm text-gray-900 dark:text-white">
                    <div className="w-36">
                      <div className="font-medium text-xs break-all" title={image.image_id}>
                        {formattedId}
                      </div>
                      {image.product_id && image.color_id && (
                        <div className="text-gray-500 dark:text-gray-400 text-xs mt-1">
                          Product: {image.product_id}
                        </div>
                      )}
                      {image.division && (
                        <div className="text-gray-500 dark:text-gray-400 text-xs mt-1">
                          Division: {image.division}
                        </div>
                      )}
                    </div>
                  </td>

                  {/* Base Image 1 */}
                  <td className="px-2 py-2">
                    <div className="w-32 h-32 xl:w-60 xl:h-60">
                      <ImageCell
                        src={image.base_image_url}
                        alt="Base image 1"
                        image={image}
                      />
                    </div>
                    {/* Show image ID info on mobile/tablet as overlay or below image */}
                    <div className="xl:hidden mt-1">
                      <div className="text-xs text-gray-600 dark:text-gray-400 font-medium" title={image.image_id}>
                        {formattedId}
                      </div>
                      {image.division && (
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {image.division}
                        </div>
                      )}
                    </div>
                  </td>

                  {/* Base Image 2 - conditionally rendered */}
                  <td className="px-2 py-2">
                    <div className="w-32 h-32 xl:w-60 xl:h-60">
                      {image.base_image_2_url ? (
                        <ImageCell
                          src={image.base_image_2_url}
                          alt="Base image 2"
                          image={image}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gray-100 dark:bg-gray-700 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600">
                          <span className="text-gray-500 dark:text-gray-400 text-xs">No Base Image 2</span>
                        </div>
                      )}
                    </div>
                  </td>

                  {/* Result Image */}
                  <td className="px-2 py-2">
                    <div className="w-32 h-32 xl:w-60 xl:h-60">
                      <ImageCell
                        src={image.result_image_url}
                        alt="Result image"
                        image={image}
                      />
                    </div>
                  </td>

                  {/* Status Controls - More compact on tablets */}
                  <td className="px-2 py-2 whitespace-nowrap">
                    <div className="flex flex-col space-y-1 xl:space-y-3">
                      {/* Status Badges */}
                      <div className="flex flex-col space-y-1">
                        {/* Annotation Status Badge */}
                        <span className={`inline-flex px-1 py-0.5 xl:px-3 xl:py-2 text-xs xl:text-sm font-semibold rounded-full capitalize ${getStatusColor(image.annotation_status)}`}>
                          {image.annotation_status}
                        </span>
                        
                        {/* Approver Status Badge - only show in approver space */}
                        {currentSpace === 'approver' && (
                          <span className={`inline-flex px-1 py-0.5 xl:px-2 xl:py-1 text-xs font-medium rounded-full capitalize ${
                            image.approver_status === 'approved' 
                              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                              : image.approver_status === 'rejected'
                              ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
                              : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300'
                          }`}>
                            {image.approver_status || 'pending'}
                          </span>
                        )}
                      </div>
                      
                      {/* Action Buttons - Different for each space */}
                      <div className="flex space-x-0.5 xl:space-x-2">
                        {currentSpace === 'annotator' ? (
                          // Annotator buttons
                          <>
                            <button
                              onClick={() => handleStatusChange(image.image_id, 'accepted')}
                              disabled={updateAnnotationMutation.isLoading}
                              className={`p-1.5 xl:p-3 rounded-lg transition-colors ${
                                image.annotation_status === 'accepted'
                                  ? 'bg-green-600 text-white'
                                  : 'bg-gray-200 dark:bg-gray-700 hover:bg-green-100 dark:hover:bg-green-800 text-gray-600 dark:text-gray-400'
                              }`}
                              title="Accept"
                            >
                              <CheckIcon className="w-3 h-3 xl:w-5 xl:h-5" />
                            </button>
                            
                            <button
                              onClick={() => handleStatusChange(image.image_id, 'rejected')}
                              disabled={updateAnnotationMutation.isLoading}
                              className={`p-1.5 xl:p-3 rounded-lg transition-colors ${
                                image.annotation_status === 'rejected'
                                  ? 'bg-red-600 text-white'
                                  : 'bg-gray-200 dark:bg-gray-700 hover:bg-red-100 dark:hover:bg-red-800 text-gray-600 dark:text-gray-400'
                              }`}
                              title="Reject"
                            >
                              <XMarkIcon className="w-3 h-3 xl:w-5 xl:h-5" />
                            </button>
                            
                            <button
                              onClick={() => handleStatusChange(image.image_id, 'regenerate')}
                              disabled={updateAnnotationMutation.isLoading}
                              className={`p-1.5 xl:p-3 rounded-lg transition-colors ${
                                image.annotation_status === 'regenerate'
                                  ? 'bg-blue-600 text-white'
                                  : 'bg-gray-200 dark:bg-gray-700 hover:bg-blue-100 dark:hover:bg-blue-800 text-gray-600 dark:text-gray-400'
                              }`}
                              title="Regenerate"
                            >
                              <ArrowPathIcon className="w-3 h-3 xl:w-5 xl:h-5" />
                            </button>
                          </>
                        ) : (
                          // Approver buttons - only approve/reject
                          <>
                            <button
                              onClick={() => handleApproverStatusChange(image.image_id, 'approved')}
                              disabled={updateApproverMutation.isLoading}
                              className={`p-1.5 xl:p-3 rounded-lg transition-colors ${
                                image.approver_status === 'approved'
                                  ? 'bg-green-600 text-white'
                                  : 'bg-gray-200 dark:bg-gray-700 hover:bg-green-100 dark:hover:bg-green-800 text-gray-600 dark:text-gray-400'
                              }`}
                              title="Approve"
                            >
                              <CheckIcon className="w-3 h-3 xl:w-5 xl:h-5" />
                            </button>
                            
                            <button
                              onClick={() => handleApproverStatusChange(image.image_id, 'rejected')}
                              disabled={updateApproverMutation.isLoading}
                              className={`p-1.5 xl:p-3 rounded-lg transition-colors ${
                                image.approver_status === 'rejected'
                                  ? 'bg-red-600 text-white'
                                  : 'bg-gray-200 dark:bg-gray-700 hover:bg-red-100 dark:hover:bg-red-800 text-gray-600 dark:text-gray-400'
                              }`}
                              title="Reject"
                            >
                              <XMarkIcon className="w-3 h-3 xl:w-5 xl:h-5" />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </td>

                  {/* Issues - More compact on tablets */}
                  <td className="px-2 py-2">
                    <div className="space-y-1 w-28 xl:w-80">
                      {/* Issues Selector */}
                      <IssueSelector
                        selectedIssues={currentIssues}
                        availableIssues={availableIssues}
                        onChange={(issues) => handleIssuesChange(image.image_id, issues)}
                        placeholder="Select issues..."
                      />
                      
                      {/* Annotation Info - Smaller on mobile/tablet */}
                      {image.annotator_id && (
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          <div className="xl:inline">By: {image.annotator_id}</div>
                          {image.annotation_updated_at && (
                            <div className="xl:inline xl:ml-2">
                              {new Date(image.annotation_updated_at).toLocaleDateString()}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Image Modal */}
      {selectedImage && (
        <ImageModal
          image={selectedImage}
          onClose={handleModalClose}
        />
      )}
    </>
  )
}

export default ImageGrid 
